import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { CheckCircle, Cancel, Close } from "@mui/icons-material";
import { GetPackageDetailById } from "api/packages";
import { GetCustomerInfo } from "api/customers";
import { getPhotoUrl } from "utils";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { CANCEL_REASON_LABEL_KEYS, STATUS_COLOR, fmtDate, statusKey } from "./shared";

const MONEY_FIELDS = new Set(["amount", "shippingFee", "deliveryFee", "finalShippingFee"]);

function KV({ label, children }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2} py={1} sx={{ borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ textAlign: "right", wordBreak: "break-word" }}>
        {typeof children === "string" || typeof children === "number" ? <Typography variant="body2">{children}</Typography> : children}
      </Box>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 2.5, mb: 0.5, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

// Detail of one package edit/cancel request — mirrors mobile-staff's
// PackageChangeRequestDetailScreen: request info, reason, field-by-field diff
// (customerId resolved to name + WhatsApp, falling back to the raw id), attached
// photos and the package as it is now. Approve/Reject are handled by the parent
// page (same handlers as the table row buttons).
export default function RequestDetailDialog({ request, open, onClose, isAdmin, busy, onApprove, onReject }) {
  const { t } = useTranslation();
  const [pkg, setPkg] = useState(null);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);

  useEffect(() => {
    if (!open || !request) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPkg(null);
      const pkgId = request.packageId?._id || request.packageId;
      const pkgRes = pkgId ? await GetPackageDetailById(pkgId).catch(() => null) : null;
      const pkgData = pkgRes?.status === 200 ? pkgRes.data : null;

      const known = {};
      if (pkgData?.customer?._id) known[pkgData.customer._id] = pkgData.customer;
      const diff = request.changes?.customerId;
      const ids = [diff?.from, diff?.to].filter(Boolean).map(String).filter((id) => !known[id]);
      await Promise.all(
        [...new Set(ids)].map(async (id) => {
          const res = await GetCustomerInfo(id).catch(() => null);
          if (res?.success && res.data) known[id] = res.data;
        })
      );
      if (cancelled) return;
      setPkg(pkgData);
      setCustomers(known);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, request]);

  if (!request) return null;

  const fieldLabel = (field) => t(`packageChangeRequest.fields.${field}`, { defaultValue: field });

  const renderValue = (field, value, isNew) => {
    const sx = isNew
      ? { color: "secondary.main", fontWeight: 700 }
      : { color: "text.secondary", textDecoration: "line-through" };
    if (value === undefined || value === null || value === "") return <Typography variant="body2" sx={sx}>—</Typography>;
    if (field === "customerId") {
      const c = customers[String(value)];
      if (!c) return <Typography variant="body2" sx={sx}>{String(value)}</Typography>;
      return (
        <Box>
          <Typography variant="body2" sx={sx}>{c.name}</Typography>
          {c.whatsapp && <Typography variant="caption" color="text.secondary">{c.whatsapp}</Typography>}
        </Box>
      );
    }
    let text = String(value);
    if (typeof value === "boolean") text = value ? t("packageChangeRequest.yes") : t("packageChangeRequest.no");
    else if (MONEY_FIELDS.has(field) && !isNaN(Number(value))) text = `฿${Number(value).toLocaleString()}`;
    else if (typeof value === "object") {
      text = Object.values(value).filter((v) => v !== null && v !== undefined && v !== "" && typeof v !== "object").join(", ") || "—";
    }
    return <Typography variant="body2" sx={sx}>{text}</Typography>;
  };

  const tracking = pkg?.tracking || request.packageId?.tracking || request.packageId;
  const changes = request.actionType === "edit" && request.changes ? Object.entries(request.changes) : [];
  const photos = request.evidencePhotos || [];
  const cancelReasonKey = CANCEL_REASON_LABEL_KEYS[request.reason];
  const typeLabel = t(request.actionType === "cancel" ? "packageChangeRequest.typeCancel" : "packageChangeRequest.typeEdit");
  const shipping = pkg && (Number(pkg.finalShippingFee) > 0 ? pkg.finalShippingFee : Number(pkg.shippingFee) > 0 ? pkg.shippingFee : null);

  // disableEnforceFocus — the reject Swal prompt has a textarea that the
  // dialog's focus trap would otherwise block.
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableEnforceFocus>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography
          variant="h5"
          component="div"
          fontWeight="bold"
          color="secondary"
          onClick={() => tracking && setTrackingOpen(true)}
          sx={{ cursor: "pointer", textDecoration: "underline", display: "inline-block" }}
        >
          {tracking}
        </Typography>
        <Typography variant="body2" color="secondary">{typeLabel}</Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <KV label={t("packageChangeRequest.columnStatus")}>
          <Chip size="small" color={STATUS_COLOR[request.status] || "default"} label={t(statusKey(request.status))} />
        </KV>
        <KV label={t("packageChangeRequest.columnType")}>{typeLabel}</KV>
        <KV label={t("packageChangeRequest.columnRequestedBy")}>
          {[request.requestedBy?.name, request.requestedBy?.whatsapp].filter(Boolean).join(" · ") || "—"}
        </KV>
        <KV label={t("packageChangeRequest.columnDate")}>{fmtDate(request.createdAt)}</KV>
        {request.status !== "pending" && (
          <>
            <KV label={t("packageChangeRequest.reviewedBy")}>{request.reviewedBy?.name || "—"}</KV>
            <KV label={t("packageChangeRequest.reviewedAt")}>{fmtDate(request.reviewedBy?.at)}</KV>
            {request.status === "rejected" && request.rejectionReason && (
              <KV label={t("packageChangeRequest.rejectionReason")}>
                <Typography variant="body2" color="error">{request.rejectionReason}</Typography>
              </KV>
            )}
          </>
        )}

        <SectionTitle>{t("packageChangeRequest.columnReason")}</SectionTitle>
        <Typography variant="body1" fontWeight={600}>{cancelReasonKey ? t(cancelReasonKey) : request.reason}</Typography>
        {request.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{request.note}</Typography>}

        {changes.length > 0 && (
          <>
            <SectionTitle>{t("packageChangeRequest.changesTitle", { count: changes.length })}</SectionTitle>
            {changes.map(([field, diff]) => (
              <Box key={field} py={1.25} sx={{ borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>{fieldLabel(field)}</Typography>
                <Box display="flex" alignItems="flex-start" gap={1} mt={0.5}>
                  <Box flex={1}>{renderValue(field, diff.from, false)}</Box>
                  <Typography variant="body2" color="text.secondary">→</Typography>
                  <Box flex={1}>{renderValue(field, diff.to, true)}</Box>
                </Box>
              </Box>
            ))}
          </>
        )}

        {photos.length > 0 && (
          <>
            <SectionTitle>{t("packageChangeRequest.photosTitle")}</SectionTitle>
            <Box display="flex" gap={1} flexWrap="wrap">
              {photos.map((photo) => (
                <a key={photo.path} href={getPhotoUrl(photo)} target="_blank" rel="noreferrer">
                  <Box
                    component="img"
                    src={getPhotoUrl(photo)}
                    alt=""
                    sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: "10px", border: "1px solid", borderColor: "divider" }}
                  />
                </a>
              ))}
            </Box>
          </>
        )}

        <SectionTitle>{t("packageChangeRequest.packageTitle")}</SectionTitle>
        {loading ? (
          <CircularProgress color="secondary" size={24} />
        ) : pkg ? (
          <>
            <KV label={t("packageChangeRequest.packageStatus")}><Chip size="small" variant="outlined" label={pkg.status} /></KV>
            <KV label={t("packageChangeRequest.fields.customerId")}>{pkg.customer?.name || "—"}</KV>
            <KV label="WhatsApp">{pkg.customer?.whatsapp || "—"}</KV>
            <KV label={t("packageChangeRequest.fields.orderId")}>{pkg.orderId || "—"}</KV>
            <KV label={t("packageChangeRequest.fields.description")}>{pkg.description || "—"}</KV>
            <KV label={t("packageChangeRequest.fields.quantity")}>{pkg.quantity != null ? String(pkg.quantity) : "—"}</KV>
            {Number(pkg.amount) > 0 && <KV label={t("packageChangeRequest.fields.amount")}>{`฿${pkg.amount}`}</KV>}
            {shipping && <KV label={t("packageChangeRequest.fields.shippingFee")}>{`฿${shipping}`}</KV>}
            {Number(pkg.deliveryFee) > 0 && <KV label={t("packageChangeRequest.fields.deliveryFee")}>{`฿${pkg.deliveryFee}`}</KV>}
            <KV label={t("packageChangeRequest.fields.station")}>{pkg.stationName || pkg.station || "—"}</KV>
            <KV label={t("packageChangeRequest.fields.isCOD")}>{pkg.isCOD ? t("packageChangeRequest.yes") : t("packageChangeRequest.no")}</KV>
            {pkg.remark && <KV label={t("packageChangeRequest.fields.remark")}>{pkg.remark}</KV>}
            <KV label={t("packageChangeRequest.created")}>{fmtDate(pkg.createdAt)}</KV>
            <KV label={t("packageChangeRequest.updated")}>{fmtDate(pkg.updatedAt)}</KV>
          </>
        ) : (
          <Typography variant="body2" color="error">{t("packageChangeRequest.packageNotFound")}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {isAdmin && request.status === "pending" && (
          <>
            <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled={busy} onClick={() => onApprove(request)}>
              {t("packageChangeRequest.approve")}
            </Button>
            <Button variant="contained" color="error" startIcon={<Cancel />} disabled={busy} onClick={() => onReject(request)}>
              {t("packageChangeRequest.reject")}
            </Button>
          </>
        )}
        <Button variant="outlined" color="secondary" onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
      <TrackingDetailDialog open={trackingOpen} trackingNumber={tracking} onClose={() => setTrackingOpen(false)} />
    </Dialog>
  );
}
